import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { AddressDTO, BuyerCustomerDTO, PaymentMethodDTO, Res } from 'core/api';
import { ActionButtonDefinition, Keypad, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import { ValidationErrors, Validator } from 'core/infrastructure';
import * as SalesOrderData from './SalesOrderData';
import * as Components from './components';
import { Col, Cols, Column, Row } from 'core/ui';
import { DateUtils } from 'core/utils';
import * as PrepaymentStatus from '../payments-processing/PrepaymentStatus';
import * as PaymentComponents from '../payments-processing/components';
import { legacyControlsFor } from 'core/ui-next/controls';
import { Modal } from 'core/ui-next/modal';

export type SalesOrderConfirmedData = {
	notes: string;
	schedulePreparation: boolean;
	deliveryDate: Nullable<Date>;
	method: PaymentMethodDTO;
	prepayAmount: number;
	paidAmount: number;
	deliveryAddress: AddressDTO;
	customerDocumentNumber: string;
};

type Props = {
	isRefund: boolean;
	notes: string;
	schedulePreparation: boolean;
	customer: BuyerCustomerDTO;
	deliveryDate: Nullable<Date>;
	deliveryAddress: Nullable<AddressDTO>;
	showSchedulePreparation: boolean;
	onCustomerChanged: (customer: BuyerCustomerDTO) => Promise<void>;
	getPendingAmount: () => number;
	maxAvailableChange: number;
	paymentMethods: PaymentMethodDTO[];
} & Modal.Actions<SalesOrderConfirmedData>;

type State = SalesOrderData.SalesOrderDataModel;

export const validate = async (model: State) => {

	const paymentErrors = {};
	const { ErrorMessage } = await PrepaymentStatus.check(model.prepaymentData);
	if (ErrorMessage)
		ValidationErrors.add(paymentErrors, 'prepaymentData', ErrorMessage);

	return ValidationErrors.concat(SalesOrderData.validateCustomerAndDeliveryDate(model), paymentErrors);
};

export default class ConfirmSalesOrderDataScreen extends PosScreenClassComponent<Props, SalesOrderData.SalesOrderDataModel> {

	private form = React.createRef<UI.Form<State>>();

	protected getScreenId(): string {
		return 'CONFIRM_SALES_ORDER_DATA';
	}

	protected getTitle(): string {
		return this.props.isRefund ? Res.Web.CONFIRM_DOCUMENT_DATA_CUSTOMER_LBL_REFUND_SALE_ORDER_TITLE() : Res.Web.CONFIRM_DOCUMENT_DATA_CUSTOMER_LBL_CREATE_SALE_ORDER_TITLE();
	}

	protected getSize(): ScreenSize {
		return { width: 1000, height: 768 };
	}

	protected getImage(): string | SvgIcon {
		return Images.CreateSalesOrder;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			this.okButton(this.onOK)
		];
	}

	protected useKeypad = () => !ThemeManager.isLargeDevice();

	private onOK = async () => {

		const valid = await this.form.current!.updateModel();
		if (valid) {

			// Aunque ya se ha estado validando, volvemos a comprobar por si el saldo del TPV se pudiera
			// quedar en negativo
			const canPay = await PrepaymentStatus.canPay(this.state.prepaymentData);
			if (!canPay)
				return;

			const deliveryDate = this.state.includeDeliveryDate
				? DateUtils.buildDateTime(this.state.deliveryDate, this.state.deliveryTime, 'TRUNC_TO_SECONDS')
				: null;

			this.close({
				notes: this.state.notes,
				deliveryDate,
				schedulePreparation: deliveryDate !== null && this.state.schedulePreparation,
				method: this.state.prepaymentData.Method!,
				prepayAmount: this.state.prepaymentData.AmountToPay,
				paidAmount: this.state.prepaymentData.PaidAmount,
				deliveryAddress: this.state.deliveryAddress,
				customerDocumentNumber: this.state.customerDocumentNumber
			});
		}
	};

	private buildPreparationComponent(): React.ReactNode {
		const { TimeBox, CheckBox } = UI.controlsFor<SalesOrderData.SalesOrderDataModel>();
		const { DatePicker } = legacyControlsFor<SalesOrderData.SalesOrderDataModel>();

		return <Cols>
			<Col width='2ex' className='text-muted text-26 m-l-md'>
				<i className='fa fa-calendar m-r-sm'></i>
			</Col>
			<Col width='*'>
				<p className='text-26 text-muted p-l'>{Res.Web.CONFIRM_DOCUMENT_DATA_DELIVERY_DATE()}</p>
				<div className='m-l'>
					<CheckBox label={Res.Web.CONFIRM_DOCUMENT_DATA_SET_DELIVERY_DATE()} linkTo={x => x.includeDeliveryDate} onValueChanged={includeDeliveryDate => this.setState({ includeDeliveryDate })}>
						<div>
							<DatePicker label={Res.Web.CONFIRM_DOCUMENT_DATA_DELIVERY_DATE_DAY()} linkTo={x => x.deliveryDate} required />
							<TimeBox label={Res.Web.CONFIRM_DOCUMENT_DATA_DELIVERY_DATE_TIME()} linkTo={x => x.deliveryTime} placeholder={'HH:mm'} required />
						</div>
					</CheckBox>
					{this.props.showSchedulePreparation && this.state.includeDeliveryDate ? <CheckBox linkTo={x => x.schedulePreparation} label={Res.Web.CONFIRM_DOCUMENT_DATA_SCHEDULE_PREPARATION_LBL()} /> : null}
				</div>
			</Col>
		</Cols>;
	}

	protected getContent(): React.ReactNode {

		const { Form } = UI.controlsFor<State>();
		const { Customer, Address, Notes, CustomerDocumentNumber } = Components.createFor<State>();
		const { Prepayment } = PaymentComponents.createFor<State>();

		const onCustomerChanged = async (customer: BuyerCustomerDTO) => {
			await this.props.onCustomerChanged(customer);
			this.setState({ deliveryAddress: customer.Address, customer }, async () => {
				await this.form.current!.remount('deliveryAddress');
				// Limpiamos errores
				await this.form.current!.updateModel();
			});
		};

		const hasCustomer = this.state.customer?.Id > 0;

		return <Form className='m-t m-r m-l' ref={this.form} model={this.state} validator={Validator.create(state => validate(state))}>
			<Row>
				<Column width='one-half'>
					<div>
						<Customer linkTo={x => x.customer} onCustomerChanged={onCustomerChanged} />
					</div>
					{hasCustomer &&
						<div className='m-t-md'>
							<Address linkTo={x => x.deliveryAddress} customer={this.state.customer} />
						</div>
					}
					<div className='m-t-md'>
						{this.buildPreparationComponent()}
					</div>
					<div className='m-t-md'>
						<Notes linkTo={x => x.notes} />
					</div>
					<div className='m-t-md'>
						<CustomerDocumentNumber linkTo={x => x.customerDocumentNumber} />
					</div>
				</Column>
				<Column width='one-half'>
					<Prepayment
						linkTo={x => x.prepaymentData}
						pendingAmount={this.props.getPendingAmount()}
						methods={this.props.paymentMethods}
						isRefund={this.props.isRefund} />

					{
						// Lo ideal sería hacer esto responsive y que saliese si y solo si hay dos columnas, pero
						// no tengo una forma fácil de hacerlo de forma coordinada con el useKeypad del PosScreenClassComponent,
						// así que me conformo con asumir que si es pantalla grande (Tablet/PC), habrá dos columnas.
						// Es mucho suponer, pero no tengo muchas más opciones ahora.
						ThemeManager.isLargeDevice() && <Keypad isFullScreen={true} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
					}
				</Column>
			</Row>
		</Form >;
	}

	createInitialState(props: Props): Nullable<State> {

		const now = new Date();
		now.setMinutes(now.getMinutes() + 15, 0, 0);
		return {
			notes: props.notes,
			deliveryDate: this.props.deliveryDate || now,
			deliveryTime: this.props.deliveryDate || now,
			includeDeliveryDate: this.props.deliveryDate !== null,
			schedulePreparation: this.props.showSchedulePreparation && this.props.schedulePreparation,
			customer: this.props.customer,
			deliveryAddress: this.props.deliveryAddress || this.props.customer.Address,
			prepaymentData: {
				PendingAmount: 0,
				PaidAmount: 0,
				AmountToPay: 0,
				Method: this.props.paymentMethods[0] || null,
				ChangeAmount: 0,
				TipAmount: 0,
				ThereIsEnoughChange: false,
				ErrorMessage: ''
			},
			customerDocumentNumber: ''
		};
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}
